import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from 'lucide-react';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { FCTransferActionsCell } from 'components/warehouse/components/fcTransfers/FCTransferActionsCell';
import {
    FC_TRANSFER_DISPLAY_FALLBACK,
    formatFCTransferEstimatedDepartureDisplay,
    getTransferNumberDisplay,
    getTransferStatusDisplay,
    isFCTransferPendingStatus,
} from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import {
    FC_TRANSFER_DIRECTION,
    FCTransferSortConfig,
    getFcFilterLabel,
} from 'components/warehouse/components/fcTransfers/FCTransferFilterConfig';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { PaginationControls, ResultsCounter } from 'shared/components/pagination';

const renderActionsCell = ({ row, table }) => {
    const meta = table.options.meta ?? {};
    const { direction, onAcceptFCTransfer, onCancelFCTransfer, onDownloadBol, onEditFCTransfer, onViewFCTransfer } =
        meta;
    const rowData = row.original;
    return (
        <FCTransferActionsCell
            direction={direction}
            onViewClick={() => onViewFCTransfer?.(rowData)}
            onEditTransfer={() => onEditFCTransfer?.(rowData)}
            onDownloadBol={() => onDownloadBol?.(rowData)}
            onCancelTransfer={() => onCancelFCTransfer?.(rowData)}
            onAccept={() => onAcceptFCTransfer?.(rowData)}
            isEditTransferDisabled={!isFCTransferPendingStatus(rowData)}
        />
    );
};

const renderStatusCell = ({ row }) => {
    const hasDiscrepancies = Boolean(row.original?.hasDiscrepancies);

    return (
        <div className="inline-flex items-center gap-1.5">
            <span>{getTransferStatusDisplay(row.original)}</span>
            {hasDiscrepancies && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex items-center" aria-label="Has Receiving Discrepancies" role="img">
                            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>Has Receiving Discrepancies</TooltipContent>
                </Tooltip>
            )}
        </div>
    );
};

const MemoizedFCTransferRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

/**
 * @param {Record<string, unknown>} row
 * @param {'OUTBOUND' | 'INBOUND'} direction
 * @returns {string}
 */
function getFcCellDisplayValue(row, direction) {
    if (direction === FC_TRANSFER_DIRECTION.INBOUND) {
        return (
            row.sourceFacilityName ??
            row.source_facility_name ??
            row.sourceFcName ??
            row.source_fc_name ??
            FC_TRANSFER_DISPLAY_FALLBACK
        );
    }
    return (
        row.destinationFacilityName ??
        row.destination_facility_name ??
        row.destinationFcName ??
        row.destination_fc_name ??
        FC_TRANSFER_DISPLAY_FALLBACK
    );
}

/**
 * @param {Record<string, unknown>} row
 * @returns {unknown}
 */
function getCreatedOnRaw(row) {
    return row.createdAt ?? row.created_at ?? row.createdOn;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string | number}
 */
function getItemsCount(row) {
    const n =
        row.numberOfItems ??
        row.number_of_items ??
        row.lineItemsCount ??
        row.line_items_count ??
        row.itemsCount ??
        row.itemCount;
    if (n === undefined || n === null || n === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return n;
}

function getExpectedTotalUnits(row) {
    return row.expectedTotalUnits ?? FC_TRANSFER_DISPLAY_FALLBACK;
}

function getReceivedTotalUnits(row) {
    return row.receivedTotalUnits ?? FC_TRANSFER_DISPLAY_FALLBACK;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
function getCreatedByDisplay(row) {
    return row.createdBy ?? FC_TRANSFER_DISPLAY_FALLBACK;
}

/**
 * TanStack table for FC Transfers list (tech spec §4, §5.3–§5.4, §6.1, §7.2).
 *
 * @param {Object} props
 * @param {'OUTBOUND' | 'INBOUND'} props.direction
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {number} [props.totalCount=0]
 * @param {boolean} [props.hasNextPage=false]
 * @param {boolean} [props.hasPrevious=false]
 * @param {() => void} [props.onNext]
 * @param {() => void} [props.onPrevious]
 * @param {number} [props.currentPage]
 * @param {number} [props.totalPages]
 * @param {number} [props.pageSize]
 * @param {(size: number) => void} [props.onPageSizeChange]
 * @param {number[]} [props.pageSizeOptions]
 * @param {Array<{id: string, desc: boolean}>} [props.sorting=[]]
 * @param {(updater: import('@tanstack/react-table').OnChangeFn<import('@tanstack/react-table').SortingState>) => void} [props.onSortingChange]
 * @param {string} [props.emptyMessage='No FC transfers found.']
 * @param {(row: Record<string, unknown>) => void} [props.onViewFCTransfer]
 * @param {(row: Record<string, unknown>) => void} [props.onEditFCTransfer]
 * @param {(row: Record<string, unknown>) => void} [props.onDownloadBol]
 * @param {(row: Record<string, unknown>) => void} [props.onCancelFCTransfer]
 * @param {(row: Record<string, unknown>) => void} [props.onAcceptFCTransfer]
 */
export function FCTransfersTable({
    currentPage,
    data = [],
    direction = FC_TRANSFER_DIRECTION.OUTBOUND,
    emptyMessage = 'No FC transfers found.',
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    onAcceptFCTransfer,
    onCancelFCTransfer,
    onDownloadBol,
    onEditFCTransfer,
    onNext,
    onPageSizeChange,
    onPrevious,
    onSortingChange,
    onViewFCTransfer,
    pageSize,
    pageSizeOptions,
    sorting,
    totalCount: totalCountProp,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(() => {
        const isSortable = (key) => Boolean(FCTransferSortConfig.sortableFields[key]);
        const fcHeader = getFcFilterLabel(direction);
        return [
            {
                id: 'fc',
                header: fcHeader,
                enableSorting: false,
                cell: ({ row }) => getFcCellDisplayValue(row.original, direction),
            },
            {
                id: 'id',
                accessorKey: 'id',
                header: 'Transfer Id',
                enableSorting: isSortable('id'),
                cell: ({ row }) => getTransferNumberDisplay(row.original),
            },
            {
                id: 'status',
                header: 'Status',
                enableSorting: false,
                cell: renderStatusCell,
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: 'Created On',
                enableSorting: isSortable('createdAt'),
                cell: ({ row }) => formatPurchaseOrderDate(getCreatedOnRaw(row.original), FC_TRANSFER_DISPLAY_FALLBACK),
            },
            {
                id: 'createdBy',
                header: 'Created By',
                enableSorting: false,
                cell: ({ row }) => getCreatedByDisplay(row.original),
            },
            {
                id: 'estimatedDepartureDate',
                accessorKey: 'estimatedDepartureDate',
                header: 'Estimated departure',
                enableSorting: isSortable('estimatedDepartureDate'),
                cell: ({ row }) => formatFCTransferEstimatedDepartureDisplay(row.original),
            },
            {
                id: 'items',
                header: 'Items',
                enableSorting: false,
                cell: ({ row }) => getItemsCount(row.original),
            },
            {
                id: 'expectedTotalUnits',
                header: 'Expected Total Units',
                enableSorting: false,
                cell: ({ row }) => getExpectedTotalUnits(row.original),
            },
            {
                id: 'receivedTotalUnits',
                header: 'Received Total Units',
                enableSorting: false,
                cell: ({ row }) => getReceivedTotalUnits(row.original),
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ];
    }, [direction]);

    const tableMeta = useMemo(
        () => ({
            direction,
            onAcceptFCTransfer,
            onCancelFCTransfer,
            onDownloadBol,
            onEditFCTransfer,
            onViewFCTransfer,
        }),
        [direction, onAcceptFCTransfer, onCancelFCTransfer, onDownloadBol, onEditFCTransfer, onViewFCTransfer],
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            sorting: sorting ?? [],
        },
        onSortingChange,
        meta: tableMeta,
    });

    const loadedCount = tableData.length;
    const totalCount = totalCountProp ?? loadedCount;
    const isRefreshing = isFetching;
    const showPagination = tableData.length > 0;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && totalCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                    <div className="shrink-0">
                        <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isRefreshing} />
                    </div>
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading FC transfers…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={`whitespace-normal break-words${canSort ? ' cursor-pointer select-none' : ''}`}
                                                    onClick={
                                                        canSort ? header.column.getToggleSortingHandler() : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex items-center gap-1">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                            {canSort &&
                                                                (() => {
                                                                    if (sorted === 'asc') {
                                                                        return <ArrowUp className="h-4 w-4 shrink-0" />;
                                                                    }
                                                                    if (sorted === 'desc') {
                                                                        return (
                                                                            <ArrowDown className="h-4 w-4 shrink-0" />
                                                                        );
                                                                    }
                                                                    return (
                                                                        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                                    );
                                                                })()}
                                                        </div>
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedFCTransferRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {showPagination && (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNextPage}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

MemoizedFCTransferRow.displayName = 'MemoizedFCTransferRow';
FCTransfersTable.displayName = 'FCTransfersTable';
